import { useEffect, useId, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ModalFooter, ModalHeader, ModalInput, ModalTextarea } from '@/components/admin/editorial-modal';
import { Button } from '@/components/ui/button';
import { Plus, Send, Stethoscope } from 'lucide-react';
import { cn } from '@/lib/utils';
import { digitsOnly, FieldLabel, MonoLabel, numericInputProps } from '@/components/editorial';
import { APPT_TYPE_TOKENS } from '@/lib/medical-display';
import { useTranslation } from 'react-i18next';
import { labelFor } from '@/i18n/labels';
import { APPT_TYPE_LABEL_KEY } from '@/lib/calendar-display';
import { formatMonthDay } from '@/i18n/intl';
import { useCompanionName } from '@/hooks/useCompanionName';
import type { CreateAppointmentRequest, MedicalAppointment } from '@/types/api';

const APPT_TYPES: MedicalAppointment['appointment_type'][] = [
  'oncology_consult',
  'chemotherapy',
  'radiation',
  'scan',
  'lab',
  'surgery',
  'physical_therapy',
  'social_worker',
  'other',
];

interface AddAppointmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: MedicalAppointment | null;
  /**
   * The full appointment list, used to prefill prep_notes/transport/provider/
   * location from the most recent past same-type appointment when adding a new
   * one. Saves parents from re-typing "bring her bag, headphones, soft blanket"
   * every cycle.
   */
  pastAppointments?: MedicalAppointment[];
  onSubmit: (data: CreateAppointmentRequest) => void;
  isSubmitting?: boolean;
}

function toLocalDatetimeInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The inverse of `toLocalDatetimeInput` (#2373). A `datetime-local` value
 * ("2026-10-09T10:00") carries no offset; sent raw, the server stored it as
 * 10:00 UTC and every view read it back hours early. Built from its parts in
 * the browser's zone, it becomes the instant the parent typed, sent as UTC
 * ISO. An unparseable value passes through for the server to reject.
 */
function fromLocalDatetimeInput(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!m) return value;
  const [, y, mo, d, h, mi, s] = m;
  const local = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? 0), 0);
  return Number.isNaN(local.getTime()) ? value : local.toISOString();
}

const EMPTY: CreateAppointmentRequest = {
  title: '',
  appointment_type: 'oncology_consult',
  provider: '',
  location: '',
  date_time: '',
  duration_mins: 60,
  prep_required: false,
  prep_notes: '',
  transport_needed: false,
  notes: '',
  add_to_calendar: true,
};

export function AddAppointmentModal({
  open,
  onOpenChange,
  editing,
  pastAppointments,
  onSubmit,
  isSubmitting,
}: AddAppointmentModalProps) {
  const { t } = useTranslation(['calendar', 'common']);
  const companionName = useCompanionName();
  const [form, setForm] = useState<CreateAppointmentRequest>(EMPTY);
  // Tracks which past appointment (if any) the prefill came from — surfaces
  // a small dismissable hint above the prep_notes field.
  const [prefillSource, setPrefillSource] = useState<MedicalAppointment | null>(null);

  // Hydrate from `editing` whenever the modal opens with an existing record.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: editing.title,
        appointment_type: editing.appointment_type,
        provider: editing.provider ?? '',
        location: editing.location ?? '',
        date_time: toLocalDatetimeInput(editing.date_time),
        duration_mins: editing.duration_mins ?? 60,
        prep_required: editing.prep_required,
        prep_notes: editing.prep_notes ?? '',
        transport_needed: editing.transport_needed,
        notes: editing.notes ?? '',
        add_to_calendar: editing.add_to_calendar,
      });
      setPrefillSource(null);
    } else {
      setForm(EMPTY);
      setPrefillSource(null);
    }
  }, [open, editing]);

  // When the user picks an appointment_type for a NEW appointment and the
  // form's prep/provider/location fields are still blank (or were last
  // populated by us), look up the most recent past appointment of the same
  // type and prefill from it. Once a parent edits any of those fields the
  // hint clears and the prefill effect stops firing — we never overwrite a
  // human edit.
  useEffect(() => {
    if (!open || editing) return;
    if (!pastAppointments?.length) return;
    // Fields are "owned by Stevens" while the prefillSource is set. If the
    // parent has edited any of them since the prefill, we treat that as user
    // input and stop suggesting.
    if (!prefillSource) {
      const hasUserInput =
        form.prep_notes ||
        form.provider ||
        form.location ||
        form.prep_required ||
        form.transport_needed;
      if (hasUserInput) return;
    } else {
      // We previously prefilled; check whether any field has diverged from
      // the prefilled source — if yes, the parent has been editing and we
      // should leave them alone.
      const diverged =
        (form.prep_notes ?? '') !== (prefillSource.prep_notes ?? '') ||
        (form.provider ?? '') !== (prefillSource.provider ?? '') ||
        (form.location ?? '') !== (prefillSource.location ?? '') ||
        form.prep_required !== prefillSource.prep_required ||
        form.transport_needed !== prefillSource.transport_needed;
      if (diverged) return;
    }

    const sameType = pastAppointments
      .filter((a) => a.appointment_type === form.appointment_type)
      .sort((a, b) => new Date(b.date_time).getTime() - new Date(a.date_time).getTime());
    const source = sameType[0];
    if (!source) {
      // No past appt of this type — clear any earlier prefill.
      if (prefillSource) {
        setForm((f) => ({ ...f, provider: '', location: '', prep_required: false, prep_notes: '', transport_needed: false }));
        setPrefillSource(null);
      }
      return;
    }
    if (prefillSource && prefillSource._id === source._id) return;

    setForm((f) => ({
      ...f,
      provider: source.provider ?? '',
      location: source.location ?? '',
      prep_required: source.prep_required,
      prep_notes: source.prep_notes ?? '',
      transport_needed: source.transport_needed,
    }));
    setPrefillSource(source);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, form.appointment_type, pastAppointments]);

  const clearPrefill = () => {
    setForm((f) => ({
      ...f,
      provider: '',
      location: '',
      prep_required: false,
      prep_notes: '',
      transport_needed: false,
    }));
    setPrefillSource(null);
  };

  const isEdit = Boolean(editing);
  const valid = form.title.trim() && form.date_time;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl flex flex-col p-0 gap-0 max-h-[90vh]">
        <ModalHeader
          icon={Stethoscope}
          eyebrow={isEdit ? t('add.editEyebrow') : t('add.newEyebrow')}
          title={isEdit ? t('add.editTitle') : t('add.newTitle')}
        />

        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-5 min-h-0">
          <Field group label={t('add.field.type')}>
            <div className="grid grid-cols-3 gap-2">
              {APPT_TYPES.map((type) => {
                const tokens = APPT_TYPE_TOKENS[type];
                const isActive = form.appointment_type === type;
                return (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setForm((f) => ({ ...f, appointment_type: type }))}
                    className={cn(
                      'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-caption text-left transition-colors',
                      isActive
                        ? 'border-stevens-indigo bg-stevens-indigo/5 text-foreground'
                        : 'border-border hover:bg-muted',
                    )}
                  >
                    <span className={cn('w-[7px] h-[7px] shrink-0 rounded-full', tokens.dot)} />
                    {labelFor(APPT_TYPE_LABEL_KEY, type, t)}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label={t('add.field.what')}>
            <ModalInput
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={t('add.placeholder.what')}
              autoFocus
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('add.field.provider')}>
              <ModalInput
                type="text"
                value={form.provider ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}
                placeholder={t('add.placeholder.provider')}
              />
            </Field>
            <Field label={t('add.field.location')}>
              <ModalInput
                type="text"
                value={form.location ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                placeholder={t('add.placeholder.location')}
              />
            </Field>
          </div>

          {/* Stacked below sm (#2419): in zh at 390 px a fixed 120 px
              duration column ran past the dialog edge and cut its label. */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
            <Field label={t('add.field.when')}>
              <ModalInput
                type="datetime-local"
                value={form.date_time}
                onChange={(e) => setForm((f) => ({ ...f, date_time: e.target.value }))}
              />
            </Field>
            <Field label={t('add.field.duration')}>
              <ModalInput
                {...numericInputProps({ max: 600 })}
                value={form.duration_mins ?? 60}
                onChange={(e) => {
                  const digits = digitsOnly(e.target.value);
                  setForm((f) => ({ ...f, duration_mins: digits === '' ? undefined : Number(digits) }));
                }}
              />
            </Field>
          </div>

          {prefillSource && (
            <div className="flex items-start gap-2 rounded-md bg-stevens-indigo/5 border border-stevens-indigo/15 px-3 py-2 text-caption">
              <span className="flex-1 text-stevens-indigo/90">
                {/* One sentence with params (the old one split around an en-US date). */}
                {t('add.prefilled', {
                  type: labelFor(APPT_TYPE_LABEL_KEY, prefillSource.appointment_type, t),
                  date: formatMonthDay(prefillSource.date_time),
                })}
              </span>
              <button
                type="button"
                onClick={clearPrefill}
                className="text-stevens-indigo/70 hover:text-stevens-indigo underline-offset-2 hover:underline"
              >
                {t('add.clearPrefill')}
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Toggle
              checked={form.prep_required ?? false}
              onChange={(v) => setForm((f) => ({ ...f, prep_required: v }))}
              label={t('add.toggle.prep')}
            />
            <Toggle
              checked={form.transport_needed ?? false}
              onChange={(v) => setForm((f) => ({ ...f, transport_needed: v }))}
              label={t('add.toggle.transport')}
            />
            <Toggle
              checked={form.add_to_calendar ?? true}
              onChange={(v) => setForm((f) => ({ ...f, add_to_calendar: v }))}
              label={t('add.toggle.calendar')}
            />
          </div>

          {form.prep_required && (
            <Field label={t('add.field.prepNotes')}>
              <ModalTextarea
                value={form.prep_notes ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, prep_notes: e.target.value }))}
                placeholder={t('add.placeholder.prepNotes')}
                rows={2}
              />
            </Field>
          )}

          <Field label={t('add.field.notes')}>
            <ModalTextarea
              value={form.notes ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder={t('add.placeholder.notes', { companion: companionName })}
              rows={2}
            />
          </Field>
        </div>

        <ModalFooter
          lead={
            <MonoLabel className="text-[11px]">
              {form.add_to_calendar ? t('add.footer.onCalendar') : t('add.footer.recordOnly')}
            </MonoLabel>
          }
        >
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            size="sm"
            variant="brand"
            disabled={!valid || isSubmitting}
            onClick={() => onSubmit({ ...form, date_time: fromLocalDatetimeInput(form.date_time) })}
          >
            {isEdit ? <Send className="h-3.5 w-3.5 mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
            {isSubmitting ? t('common:states.saving') : isEdit ? t('common:actions.saveChanges') : t('add.submit')}
          </Button>
        </ModalFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A field's heading and its control. A single input sits inside a <label>.
 * A set of chips is a `group` (#2434): a <label> wrapping buttons forwards a
 * tap on its text to the FIRST button, which silently changed the type, and
 * gave that button the heading plus every chip as its name. The group is
 * named by the heading instead, and each chip names only itself.
 */
function Field({ label, group, children }: { label: string; group?: boolean; children: React.ReactNode }) {
  const headingId = useId();
  if (group) {
    return (
      <div role="group" aria-labelledby={headingId} className="block">
        <FieldLabel as="span" id={headingId} className="mb-1.5">{label}</FieldLabel>
        {children}
      </div>
    );
  }
  return (
    <label className="block">
      <FieldLabel as="span" className="mb-1.5">{label}</FieldLabel>
      {children}
    </label>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-body">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-input text-stevens-indigo focus:ring-stevens-indigo"
      />
      <span>{label}</span>
    </label>
  );
}
